import {useEffect,useMemo,useState} from 'react';
import {Helmet} from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  bodySystems,
  builderParts,
  builderTerms,
  medicalTerms,
  termCategories,
} from '../data/medicalTerms';
import './TermDictionary.css';

const {
  FiArrowLeft,
  FiBookOpen,
  FiChevronDown,
  FiRotateCcw,
  FiSearch,
  FiShuffle,
} = FiIcons;

const typeLabels = {
  prefix: 'Prefix',
  root: 'Root',
  suffix: 'Suffix',
  abbreviation: 'Abbreviation',
};

function formatCommaSpacing(value = '') {
  return value.replace(/,\s*/g, ', ');
}

function findTerm(id) {
  return medicalTerms.find((item) => item.id === id);
}

function TermBuilder({onSelectSample}) {
  const [parts,setParts] = useState({
    prefix: '',
    root: 'root-cardi',
    suffix: 'suffix-itis',
  });

  const selectedParts = {
    prefix: findTerm(parts.prefix),
    root: findTerm(parts.root),
    suffix: findTerm(parts.suffix),
  };

  const updatePart = (type,value) => {
    setParts((current) => ({
      ...current,
      [type]: value,
    }));
  };

  const combinedMeaning = [
    selectedParts.prefix?.meaning,
    selectedParts.root?.meaning,
    selectedParts.suffix?.meaning,
  ]
    .filter(Boolean)
    .map(formatCommaSpacing)
    .join(', ');

  return (
    <section className="term-builder">
      <div className="builder-heading">
        <div>
          <span className="content-label">Featured utility</span>
          <h2>Deconstruct a medical term</h2>
          <p>
            Select word parts or load a sample to see how the pieces create
            meaning.
          </p>
        </div>
        <SafeIcon icon={FiBookOpen} />
      </div>

      <div className="sample-terms">
        {builderTerms.map((sample) => (
          <button
            key={sample.name}
            onClick={() => onSelectSample(sample,setParts)}
          >
            {sample.name}
          </button>
        ))}
      </div>

      <div className="builder-selects">
        {[
          ['prefix','Prefix',builderParts.prefix],
          ['root','Root word',builderParts.root],
          ['suffix','Suffix',builderParts.suffix],
        ].map(([type,label,options]) => (
          <label className="builder-select" key={type}>
            <span>{label}</span>
            <div>
              <select
                value={parts[type]}
                onChange={(event) => updatePart(type,event.target.value)}
                aria-label={`Select ${label.toLowerCase()}`}
              >
                {options.map((option) => (
                  <option value={option.value} key={option.value || `${type}-none`}>
                    {option.label}
                  </option>
                ))}
              </select>
              <SafeIcon icon={FiChevronDown} />
            </div>
          </label>
        ))}
      </div>

      <div className="builder-breakdown">
        <div className="breakdown-piece piece-prefix">
          <span>Prefix</span>
          <strong>{selectedParts.prefix?.term || 'None selected'}</strong>
          <p>{selectedParts.prefix?.meaning || 'Choose a prefix to add context.'}</p>
        </div>
        <div className="breakdown-piece piece-root">
          <span>Root</span>
          <strong>{selectedParts.root?.term || 'None selected'}</strong>
          <p>{selectedParts.root?.meaning || 'Choose a root to identify the body part.'}</p>
        </div>
        <div className="breakdown-piece piece-suffix">
          <span>Suffix</span>
          <strong>{selectedParts.suffix?.term || 'None selected'}</strong>
          <p>{selectedParts.suffix?.meaning || 'Choose a suffix to describe the condition or procedure.'}</p>
        </div>
      </div>

      <div className="builder-result">
        <span className="content-label">Combined meaning</span>
        <strong>
          {combinedMeaning || 'Select word parts to build a definition'}
        </strong>
        <p>
          {selectedParts.suffix?.relevance ||
            selectedParts.root?.relevance ||
            'This breakdown provides a study aid; confirm the full clinical meaning in context.'}
        </p>
      </div>
    </section>
  );
}

function TermCard({term,onFlip,flipped}) {
  return (
    <article className={flipped ? 'term-card is-flipped' : 'term-card'}>
      <button
        className="term-card-face term-card-front"
        onClick={() => onFlip(term.id)}
      >
        <div className="term-card-top">
          <strong>{term.term}</strong>
          <span className={`term-badge badge-${term.type}`}>
            {typeLabels[term.type]}
          </span>
        </div>
        <p>{formatCommaSpacing(term.meaning)}</p>
        <span className="term-card-hint">
          Click to reveal coding context
        </span>
      </button>
      <button
        className="term-card-face term-card-back"
        onClick={() => onFlip(term.id)}
      >
        <span className="content-label">Clinical example</span>
        <strong>{formatCommaSpacing(term.example)}</strong>
        <p>{formatCommaSpacing(term.relevance)}</p>
        <span className="term-system">{term.system}</span>
      </button>
    </article>
  );
}

function TermDictionary() {
  const [query,setQuery] = useState('');
  const [debouncedQuery,setDebouncedQuery] = useState('');
  const [category,setCategory] = useState('all');
  const [system,setSystem] = useState(bodySystems[0]);
  const [quizMode,setQuizMode] = useState(false);
  const [flippedCards,setFlippedCards] = useState({});
  const [quizIndex,setQuizIndex] = useState(0);
  const [quizFlipped,setQuizFlipped] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedQuery(query.trim().toLowerCase()),
      180,
    );
    return () => window.clearTimeout(timer);
  },[query]);

  useEffect(() => {
    window.prerenderReady = false;
    const frame = window.requestAnimationFrame(() => {
      window.prerenderReady = true;
    });
    return () => {
      window.cancelAnimationFrame(frame);
      window.prerenderReady = false;
    };
  },[]);

  const filteredTerms = useMemo(
    () =>
      medicalTerms.filter((term) => {
        const matchesCategory =
          category === 'all' || term.type === category;
        const matchesSystem =
          system === bodySystems[0] || term.system === system;
        const searchable = `${term.term} ${term.meaning} ${term.example} ${term.system}`
          .toLowerCase();

        return (
          matchesCategory &&
          matchesSystem &&
          searchable.includes(debouncedQuery)
        );
      }),
    [category,debouncedQuery,system],
  );

  const quizTerm =
    filteredTerms[quizIndex % Math.max(filteredTerms.length,1)];

  const toggleCard = (id) => {
    setFlippedCards((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const loadSample = (sample,setParts) => {
    setParts({
      prefix: sample.prefix,
      root: sample.root,
      suffix: sample.suffix,
    });
  };

  const shuffleQuiz = () => {
    if (!filteredTerms.length) return;
    setQuizIndex(Math.floor(Math.random() * filteredTerms.length));
    setQuizFlipped(false);
  };

  return (
    <>
      <Helmet>
        <title>Medical Terminology Dictionary &amp; Search | Study Prep</title>
        <meta
          name="description"
          content="Searchable medical terminology dictionary covering root words, prefixes, suffixes, and clinical definitions."
        />
      </Helmet>

      <main className="dictionary-page">
        <section className="dictionary-hero">
          <div className="container">
            <button
              className="back-link"
              onClick={() => window.history.back()}
            >
              <SafeIcon icon={FiArrowLeft} /> Back to Study Tools
            </button>
            <span style={{marginLeft: '10px'}} className="eyebrow">
              CPC and CCA exam preparation
            </span>
            <h1>Medical Term Dictionary</h1>
            <p>
              Break unfamiliar terms into prefixes, roots, and suffixes to decode
              diagnostic and procedural language with confidence.
            </p>
          </div>
        </section>

        <section className="dictionary-section">
          <div className="container">
            <TermBuilder onSelectSample={loadSample} />

            <div className="dictionary-toolbar">
              <div className="dictionary-search">
                <SafeIcon icon={FiSearch} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search terms, meanings, examples, or systems..."
                  aria-label="Search medical terminology"
                />
              </div>

              <label className="system-filter">
                <span>Body system</span>
                <select
                  value={system}
                  onChange={(event) => setSystem(event.target.value)}
                >
                  {bodySystems.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="dictionary-controls">
              <div
                className="category-tabs"
                role="tablist"
                aria-label="Term categories"
              >
                {termCategories.map((item) => (
                  <button
                    key={item.key}
                    className={category === item.key ? 'is-active' : ''}
                    onClick={() => setCategory(item.key)}
                    role="tab"
                    aria-selected={category === item.key}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <button
                className={quizMode ? 'quiz-toggle is-active' : 'quiz-toggle'}
                onClick={() => setQuizMode((value) => !value)}
              >
                <SafeIcon icon={quizMode ? FiRotateCcw : FiShuffle} />
                {quizMode ? 'Dictionary Mode' : 'Quick Quiz Mode'}
              </button>
            </div>

            {!quizMode ? (
              <section className="term-grid" aria-live="polite">
                {filteredTerms.map((term) => (
                  <TermCard
                    key={term.id}
                    term={term}
                    flipped={Boolean(flippedCards[term.id])}
                    onFlip={toggleCard}
                  />
                ))}
                {!filteredTerms.length && (
                  <div className="empty-state">
                    <h2>No results found</h2>
                    <p>Try a broader search or reset one of the filters.</p>
                  </div>
                )}
              </section>
            ) : (
              <section className="term-quiz">
                <div className="quiz-mode-heading">
                  <div>
                    <span className="content-label">Active recall</span>
                    <h2>What does this word part mean?</h2>
                  </div>
                  <button onClick={shuffleQuiz}>
                    <SafeIcon icon={FiShuffle} /> Shuffle
                  </button>
                </div>

                {quizTerm ? (
                  <button
                    className={
                      quizFlipped ? 'quiz-term is-flipped' : 'quiz-term'
                    }
                    onClick={() => setQuizFlipped((value) => !value)}
                  >
                    <span className="quiz-term-front">
                      <small>{typeLabels[quizTerm.type]}</small>
                      <strong>{quizTerm.term}</strong>
                      <span>Click to reveal the answer</span>
                    </span>
                    <span className="quiz-term-back">
                      <small>Meaning</small>
                      <strong>{formatCommaSpacing(quizTerm.meaning)}</strong>
                      <span>
                        {quizTerm.system} · {formatCommaSpacing(quizTerm.example)}
                      </span>
                    </span>
                  </button>
                ) : (
                  <div className="empty-state">
                    No terms match the current filters.
                  </div>
                )}
              </section>
            )}

            <p className="dictionary-note">
              Study aid only. Always verify terminology, documentation, and
              current AMA, CMS, payer, and code-set guidance before coding.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default TermDictionary;